import { useEffect, useRef } from "react";
import { useBusiness } from "@/core/business";
import { useEnv } from "@/core/env";
import { getUserFullName } from "@/core/utils/name";
import { useGetMyUser } from "@/user/query";
import { isPylonAccessAllowed } from "./pylon-access";

const PYLON_SCRIPT_ID = "pylon-chat-widget-script";
const PYLON_WIDGET_URL = "https://widget.usepylon.com/widget";
const DEV_PYLON_APP_ID = "88647c75-714a-480d-a3f4-2e5a571c62e0";

type PylonQueueFunction = ((command: string, ...args: unknown[]) => void) & {
  q: unknown[][];
  e: (args: unknown[]) => void;
};

const ensurePylonQueue = () => {
  if (window.Pylon) {
    return;
  }

  const pylonQueue = ((...args: unknown[]) => {
    pylonQueue.e(args);
  }) as PylonQueueFunction;

  pylonQueue.q = [];
  pylonQueue.e = (args: unknown[]) => {
    pylonQueue.q.push(args);
  };

  window.Pylon = pylonQueue;
};

const ensurePylonScript = (appId: string) => {
  if (document.getElementById(PYLON_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement("script");
  script.id = PYLON_SCRIPT_ID;
  script.async = true;
  script.type = "text/javascript";
  script.src = `${PYLON_WIDGET_URL}/${appId}`;

  const firstScriptTag = document.getElementsByTagName("script")[0];
  if (firstScriptTag?.parentNode) {
    firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
    return;
  }

  document.body.appendChild(script);
};

interface PylonProviderProps {
  children: React.ReactNode;
  hasAccess: boolean;
}

export const PylonProvider = ({ children, hasAccess }: PylonProviderProps) => {
  const { data: user } = useGetMyUser();
  const { businessId } = useBusiness();
  const { appEnv, pylonAppId } = useEnv();
  const initializedKeyRef = useRef<string | null>(null);
  const hasWarnedMissingIdentityHashRef = useRef(false);
  useEffect(() => {
    const resolvedPylonAppId =
      pylonAppId || (appEnv === "development" ? DEV_PYLON_APP_ID : "");

    if (!hasAccess || !user || !resolvedPylonAppId) {
      return;
    }

    const userEmailHash = user.pylon_email_hash || undefined;
    if (
      appEnv === "production" &&
      !userEmailHash &&
      !hasWarnedMissingIdentityHashRef.current
    ) {
      hasWarnedMissingIdentityHashRef.current = true;
      console.warn(
        "Pylon identity verification hash is missing for this user. " +
          "If identity verification is enabled in Pylon, backend must return a per-user pylon_email_hash.",
      );
    }

    window.pylon = {
      chat_settings: {
        app_id: resolvedPylonAppId,
        email: user.email,
        email_hash: userEmailHash,
        name:
          user.first_name && user.last_name ? getUserFullName(user) : undefined,
        account_external_id: businessId || undefined,
      },
    };

    const initializationKey = `${resolvedPylonAppId}:${user.id}`;
    if (initializedKeyRef.current !== initializationKey) {
      ensurePylonQueue();
      ensurePylonScript(resolvedPylonAppId);

      initializedKeyRef.current = initializationKey;
    }

    window.Pylon?.("onShow", () => {
      if (!isPylonAccessAllowed()) window.Pylon?.("hide");
    });

    window.Pylon?.("setNewIssueCustomFields", {
      user_id: user.id,
      role: user.role || undefined,
      parentable_type: user.parentable_type || undefined,
      parentable_id: user.parentable_id || undefined,
      business_id: businessId || undefined,
    });
    window.Pylon?.("hideChatBubble");
  }, [appEnv, businessId, hasAccess, pylonAppId, user]);

  return <>{children}</>;
};
